
// Utility functions for handling user location
import { UserLocation } from "@/hooks/use-location-handling";

// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

// Save user location to local storage
export const saveUserLocation = (location: UserLocation) => {
  const dataToSave = {
    ...location,
    timestamp: Date.now(),
  };
  localStorage.setItem('user_location', JSON.stringify(dataToSave));
};

// Get cached user location
export const getCachedLocation = (): UserLocation | null => {
  const cachedLocation = localStorage.getItem('user_location');
  
  if (!cachedLocation) return null;
  
  try {
    const parsedCache = JSON.parse(cachedLocation);
    
    // Check if cache is expired
    if (Date.now() - parsedCache.timestamp > CACHE_EXPIRATION) {
      localStorage.removeItem('user_location');
      return null;
    }
    
    return {
      country: parsedCache.country,
      city: parsedCache.city,
      region: parsedCache.region,
      currency: parsedCache.currency,
    };
  } catch (error) {
    console.error("Error parsing cached location:", error);
    return null;
  }
};

// Clear the location cache
export const clearLocationCache = () => {
  console.info("Clearing location cache");
  localStorage.removeItem('user_location');
};

// Save user location preference - with improved parameter handling
export const saveUserLocationPreference = (userId: string, location?: UserLocation | string, region?: string, city?: string) => {
  // This would typically be an API call to save the user's location preference
  // For now, we'll just save it to local storage
  if (typeof location === 'string') {
    // Handle the case where location is passed as a country string
    const locationData = { country: location, region, city, currency: null };
    localStorage.setItem(`user_${userId}_location_preference`, JSON.stringify(locationData));
  } else if (location) {
    // Handle the case where location is a UserLocation object
    localStorage.setItem(`user_${userId}_location_preference`, JSON.stringify(location));
  }
  return Promise.resolve(true);
};

// Get user's saved location preference
export const getUserLocationPreference = (userId: string): UserLocation | null => {
  const savedPreference = localStorage.getItem(`user_${userId}_location_preference`);
  
  if (!savedPreference) return null;
  
  try {
    return JSON.parse(savedPreference);
  } catch (error) {
    console.error("Error parsing location preference:", error);
    return null;
  }
};

// Fetch user location from IP address
export const getUserLocationFromIP = async (): Promise<{
  country?: string;
  region?: string;
  city?: string;
}> => {
  try {
    console.log("Fetching location from IP API...");
    const response = await fetch('https://ipapi.co/json/');
    
    if (!response.ok) {
      throw new Error(`Error fetching location: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      country: data.country_name,
      region: data.region,
      city: data.city
    };
  } catch (error) {
    console.error("Error fetching location from IP:", error);
    return {}; // Return empty object in case of error
  }
};
